import { useId, useRef, useState } from "react";
import type { CustomerDeliveryField } from "@shared-core/domain/customer-basket-presentation";
import type {
  CustomerCheckoutDeliveries,
  CustomerCheckoutDelivery,
  CustomerCheckoutDeliveryLevel,
  CustomerCollectionPoint,
} from "@shared-core/data/customer-basket-types";
import {
  collectionPointText,
  deliveryDestinationText,
  deliveryLinesText,
  isChoosableServiceLevel,
  NO_PICK,
  type DeliveryPick,
} from "@shared-core/domain/checkout-delivery";
import { deliveryArrivalSentence } from "@shared-core/domain/delivery-arrival";
import { formatCount, formatQuotedPrice } from "@shared-core/domain/formatters";
import { Card } from "../../../app/components/primitives/card";
import { Button } from "../../../app/components/primitives/button";
import { Input } from "../../../app/components/primitives/input";
import { Label } from "../../../app/components/primitives/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../app/components/primitives/radio-group";
import { cn } from "../../../app/components/primitives/utils";

// The checkout's Delivery section (order to invoice doc 104 §9.3, the mock-up's "Checkout" tab): a supplier's delivery
// included in its price, then each delivery of ours with the levels to choose from at their price and
// arrival (never a courier), "Collect from us" beside them where we have a collection point, and who receives it.
// A delivery not yet priced says so in the server's words and the order can still be placed. Everything shown is
// the preview's; the page sends the person's picks back to it each time one changes.

/** What a pick changes, and whether the preview must be read again for it (a typed name does not move a price). */
export type OnDeliveryPick = (
  deliveryRef: string,
  patch: Partial<DeliveryPick>,
  options?: { preview?: boolean },
) => void;

/** The server's refusals of a delivery's inputs, by delivery, shown beside the input they name. */
export type DeliveryErrors = Record<
  string,
  Partial<Record<CustomerDeliveryField, string>>
>;

export function CheckoutDeliverySection({
  previewed,
  error,
  picks,
  errors,
  movedPrices,
  disabled,
  onPick,
}: {
  /** Null while the first preview is being read. */
  previewed: CustomerCheckoutDeliveries | null;
  /** Why the preview could not be read, in the server's words. */
  error: string | null;
  picks: Record<string, DeliveryPick>;
  errors: DeliveryErrors;
  /** The server's sentence for each delivery whose price is no longer the one shown, by delivery. */
  movedPrices: Record<string, string>;
  disabled: boolean;
  onPick: OnDeliveryPick;
}) {
  // A collected delivery comes back without its levels: remember them so the person can go back to one.
  const levelsSeen = useRef(new Map<string, CustomerCheckoutDeliveryLevel[]>());
  for (const d of previewed?.deliveries ?? []) {
    if (d.levels.length > 0) levelsSeen.current.set(d.delivery_ref, d.levels);
  }
  return (
    <Card className="gap-3 p-5" aria-labelledby="checkout-delivery-heading">
      <h2 id="checkout-delivery-heading" className="text-lg font-semibold">
        Delivery
      </h2>
      {error && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          {error}
        </p>
      )}
      {!previewed && !error && (
        <p className="text-sm text-muted-foreground">Loading…</p>
      )}
      {previewed && (
        <div className="divide-y">
          {previewed.deliveries.map((delivery) =>
            delivery.status === "included" ? (
              <IncludedDelivery
                key={delivery.delivery_ref}
                delivery={delivery}
              />
            ) : (
              <DeliveryBlock
                key={delivery.delivery_ref}
                delivery={delivery}
                levels={
                  delivery.levels.length > 0
                    ? delivery.levels
                    : (levelsSeen.current.get(delivery.delivery_ref) ?? [])
                }
                points={previewed.collection_points}
                pick={picks[delivery.delivery_ref]}
                errors={errors[delivery.delivery_ref] ?? {}}
                movedPrice={movedPrices[delivery.delivery_ref]}
                disabled={disabled}
                onPick={onPick}
              />
            ),
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * A supplier's delivery, in its price: "Leaflets, A5 · 5,000" then "5,000 to Acme Leeds HQ, LS1 2AB · delivery
 * included", with the level and arrival only when the server knows them. Nothing to choose.
 */
function IncludedDelivery({
  delivery,
}: {
  delivery: CustomerCheckoutDelivery;
}) {
  const destination = deliveryDestinationText(delivery);
  const only = delivery.lines.length === 1 ? delivery.lines[0]! : null;
  const where = destination
    ? only
      ? `${formatCount(only.quantity)} to ${destination}`
      : `To ${destination}`
    : null;
  const level = delivery.service_level_label;
  const included = level
    ? `${level}, delivery included`
    : where
      ? "delivery included"
      : "Delivery included";
  const reading = [
    where,
    included,
    deliveryArrivalSentence(delivery.arrives_on, delivery.arrives_by),
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="space-y-0.5 py-2 text-sm first:pt-0 last:pb-0">
      <p className="font-medium">{deliveryLinesText(delivery.lines)}</p>
      <p className="text-muted-foreground">{reading}</p>
    </div>
  );
}

const COLLECT = "collect";
const DELIVER = "deliver";
const levelValue = (level: string) => `level:${level}`;

function DeliveryBlock({
  delivery,
  levels,
  points,
  pick: picked,
  errors,
  movedPrice,
  disabled,
  onPick,
}: {
  delivery: CustomerCheckoutDelivery;
  levels: CustomerCheckoutDeliveryLevel[];
  points: CustomerCollectionPoint[];
  /** Undefined until the person changes this delivery: it stands as the server has it. */
  pick: DeliveryPick | undefined;
  errors: Partial<Record<CustomerDeliveryField, string>>;
  /** The server's sentence when this delivery's price is no longer the one the person was shown. */
  movedPrice: string | undefined;
  disabled: boolean;
  onPick: OnDeliveryPick;
}) {
  const id = useId();
  const headingId = `${id}-heading`;
  const pick = picked ?? NO_PICK;
  // The person's pick shows at once; the server's answer follows.
  const isCollected = picked
    ? picked.isCollected
    : delivery.status === "collection";
  const canCollect = points.length > 0;
  const isNotYetPriced = delivery.status === "not_yet_priced";
  const destination = deliveryDestinationText(delivery);
  const lines = deliveryLinesText(delivery.lines);
  const heading =
    isCollected || !destination ? lines : `${lines} to ${destination}`;

  const value = isCollected
    ? COLLECT
    : levels.length > 0
      ? levelValue(pick.serviceLevel ?? delivery.service_level ?? "")
      : DELIVER;
  const hasChoice = levels.length > 0 || canCollect;
  const choiceError = errors.service_level ?? errors.collect;
  const choiceErrorId = `${id}-choice-error`;

  function choose(next: string) {
    if (next === COLLECT) {
      onPick(delivery.delivery_ref, { isCollected: true });
      return;
    }
    const level = next.startsWith("level:") ? next.slice(6) : null;
    onPick(delivery.delivery_ref, {
      isCollected: false,
      serviceLevel: level && isChoosableServiceLevel(level) ? level : null,
    });
  }

  return (
    <div className="space-y-2 py-2 text-sm first:pt-0 last:pb-0">
      <p id={headingId} className="font-medium">
        {heading}
      </p>
      {movedPrice && (
        <p
          data-testid="delivery-price-moved"
          className="rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
        >
          {movedPrice}
        </p>
      )}
      {delivery.status === "address_required" && (
        <p className="text-muted-foreground">Choose the delivery address.</p>
      )}
      {isNotYetPriced && !hasChoice && (
        <p className="text-muted-foreground">{delivery.not_priced_message}</p>
      )}
      {hasChoice && (
        <RadioGroup
          value={value}
          onValueChange={choose}
          disabled={disabled}
          aria-labelledby={headingId}
          aria-describedby={choiceError ? choiceErrorId : undefined}
          className="gap-0 divide-y overflow-hidden rounded-lg border"
        >
          {levels.map((level) => (
            <ChoiceRow
              key={level.service_level}
              hasError={choiceError !== undefined}
              id={`${id}-${level.service_level}`}
              value={levelValue(level.service_level)}
              isChecked={value === levelValue(level.service_level)}
              label={level.label}
              detail={deliveryArrivalSentence(
                level.arrives_on,
                level.arrives_by,
              )}
              price={formatQuotedPrice(level.price_pence)}
            />
          ))}
          {levels.length === 0 && (
            <ChoiceRow
              hasError={choiceError !== undefined}
              id={`${id}-deliver`}
              value={DELIVER}
              isChecked={value === DELIVER}
              label="Delivery"
              detail={isNotYetPriced ? delivery.not_priced_message : null}
              price={null}
            />
          )}
          {canCollect && (
            <ChoiceRow
              hasError={choiceError !== undefined}
              id={`${id}-collect`}
              value={COLLECT}
              isChecked={value === COLLECT}
              label="Collect from us"
              detail={null}
              price={formatQuotedPrice(0)}
            />
          )}
        </RadioGroup>
      )}
      {choiceError && (
        <p id={choiceErrorId} className="text-red-700 dark:text-red-400">
          {choiceError}
        </p>
      )}
      {isCollected ? (
        <Collection
          delivery={delivery}
          points={points}
          pick={pick}
          errors={errors}
          disabled={disabled}
          onPick={onPick}
        />
      ) : (
        <Recipient
          delivery={delivery}
          pick={pick}
          errors={errors}
          disabled={disabled}
          onPick={onPick}
        />
      )}
    </div>
  );
}

function ChoiceRow({
  id,
  value,
  isChecked,
  hasError,
  label,
  detail,
  price,
}: {
  id: string;
  value: string;
  isChecked: boolean;
  hasError: boolean;
  label: string;
  detail: string | null;
  price: string | null;
}) {
  return (
    <Label
      htmlFor={id}
      className={cn(
        "grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 px-3 py-2 leading-normal font-normal",
        isChecked && "bg-primary/5",
      )}
    >
      <RadioGroupItem
        id={id}
        value={value}
        className="mt-0.5"
        aria-invalid={hasError ? true : undefined}
        // The three parts sit in their own boxes; read aloud, they need a pause between them.
        aria-label={[label, detail, price].filter(Boolean).join(", ")}
      />
      {/* The name and its arrival share a line; the arrival drops under the name only when the width demands it. */}
      <span className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium">{label}</span>
        {detail && <span className="text-muted-foreground">{detail}</span>}
      </span>
      {price && <span className="font-medium tabular-nums">{price}</span>}
    </Label>
  );
}

/** "Recipient: Jo Smith, 0113 496 0000 · Change", the delivery instructions, and the fields Change opens. */
function Recipient({
  delivery,
  pick,
  errors,
  disabled,
  onPick,
}: {
  delivery: CustomerCheckoutDelivery;
  pick: DeliveryPick;
  errors: Partial<Record<CustomerDeliveryField, string>>;
  disabled: boolean;
  onPick: OnDeliveryPick;
}) {
  const id = useId();
  const [draft, setDraft] = useState<{
    name: string;
    phone: string;
    email: string;
  } | null>(null);
  const shown = [delivery.recipient_name, delivery.recipient_phone]
    .filter(Boolean)
    .join(", ");
  const recipientError =
    errors.recipient_name ?? errors.recipient_phone ?? errors.recipient_email;

  if (draft) {
    const field = (key: keyof typeof draft, label: string, type: string) => {
      const error = errors[`recipient_${key}`];
      return (
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-${key}`}>{label}</Label>
          <Input
            id={`${id}-${key}`}
            type={type}
            value={draft[key]}
            disabled={disabled}
            autoComplete="off"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-${key}-error` : undefined}
            onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
          />
          {error && (
            <p
              id={`${id}-${key}-error`}
              className="text-red-700 dark:text-red-400"
            >
              {error}
            </p>
          )}
        </div>
      );
    };
    return (
      <fieldset className="space-y-2 border-t pt-2">
        <legend className="sr-only">Recipient</legend>
        <div className="grid gap-3 sm:grid-cols-3">
          {field("name", "Name", "text")}
          {field("phone", "Phone", "tel")}
          {field("email", "Email", "email")}
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            disabled={disabled}
            onClick={() => {
              onPick(delivery.delivery_ref, { recipient: draft });
              setDraft(null);
            }}
          >
            Done
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setDraft(null)}
          >
            Cancel
          </Button>
        </div>
      </fieldset>
    );
  }

  return (
    <div className="space-y-0.5 border-t pt-2">
      <p>
        Recipient: {shown || "—"}{" "}
        <button
          type="button"
          className="text-primary hover:underline disabled:opacity-50"
          disabled={disabled}
          aria-label={`Change the recipient of ${deliveryLinesText(delivery.lines)}`}
          aria-describedby={
            recipientError ? `${id}-recipient-error` : undefined
          }
          onClick={() =>
            setDraft(
              pick.recipient ?? {
                name: delivery.recipient_name ?? "",
                phone: delivery.recipient_phone ?? "",
                email: delivery.recipient_email ?? "",
              },
            )
          }
        >
          Change
        </button>
      </p>
      {recipientError && (
        <p
          id={`${id}-recipient-error`}
          className="text-red-700 dark:text-red-400"
        >
          {recipientError}
        </p>
      )}
      {delivery.delivery_instructions && (
        <p className="text-muted-foreground">
          {delivery.delivery_instructions}
        </p>
      )}
    </div>
  );
}

/** Where it is collected from (one point: shown; several: chosen) and "Who's collecting". */
function Collection({
  delivery,
  points,
  pick,
  errors,
  disabled,
  onPick,
}: {
  delivery: CustomerCheckoutDelivery;
  points: CustomerCollectionPoint[];
  pick: DeliveryPick;
  errors: Partial<Record<CustomerDeliveryField, string>>;
  disabled: boolean;
  onPick: OnDeliveryPick;
}) {
  const id = useId();
  const collector = pick.collector ?? {
    name:
      delivery.status === "collection" ? (delivery.recipient_name ?? "") : "",
    phone:
      delivery.status === "collection" ? (delivery.recipient_phone ?? "") : "",
  };
  const isPointOwed = points.length > 1 && !pick.collectionPointId;
  const pointError = errors.collection_point_id;

  return (
    <div className="space-y-2 border-t pt-2">
      {points.length === 1 ? (
        <p>Collect from {collectionPointText(points[0]!)}</p>
      ) : (
        <div className="space-y-1.5">
          <p id={`${id}-points`} className="font-medium">
            Collect from
          </p>
          <RadioGroup
            value={pick.collectionPointId ?? ""}
            onValueChange={(pointId) =>
              onPick(delivery.delivery_ref, { collectionPointId: pointId })
            }
            disabled={disabled}
            aria-labelledby={`${id}-points`}
            aria-describedby={
              pointError
                ? `${id}-point-error`
                : isPointOwed
                  ? `${id}-point-owed`
                  : undefined
            }
            className="gap-2"
          >
            {points.map((p) => (
              <Label
                key={p.id}
                htmlFor={`${id}-${p.id}`}
                className="cursor-pointer gap-2 font-normal"
              >
                <RadioGroupItem
                  id={`${id}-${p.id}`}
                  value={p.id}
                  aria-invalid={pointError ? true : undefined}
                />
                {collectionPointText(p)}
              </Label>
            ))}
          </RadioGroup>
          {pointError ? (
            <p
              id={`${id}-point-error`}
              className="text-red-700 dark:text-red-400"
            >
              {pointError}
            </p>
          ) : (
            isPointOwed && (
              <p id={`${id}-point-owed`} className="text-muted-foreground">
                Choose where to collect from.
              </p>
            )
          )}
        </div>
      )}
      <fieldset className="space-y-2">
        <legend className="font-medium">Who&apos;s collecting</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-name`}>Name</Label>
            <Input
              id={`${id}-name`}
              value={collector.name}
              disabled={disabled}
              autoComplete="off"
              aria-invalid={errors.recipient_name ? true : undefined}
              aria-describedby={
                errors.recipient_name ? `${id}-name-error` : undefined
              }
              onChange={(e) =>
                onPick(
                  delivery.delivery_ref,
                  { collector: { ...collector, name: e.target.value } },
                  { preview: false },
                )
              }
              onBlur={() => onPick(delivery.delivery_ref, { collector })}
            />
            {errors.recipient_name && (
              <p
                id={`${id}-name-error`}
                className="text-red-700 dark:text-red-400"
              >
                {errors.recipient_name}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${id}-phone`}>Phone</Label>
            <Input
              id={`${id}-phone`}
              type="tel"
              value={collector.phone}
              disabled={disabled}
              autoComplete="off"
              aria-invalid={errors.recipient_phone ? true : undefined}
              aria-describedby={
                errors.recipient_phone ? `${id}-phone-error` : undefined
              }
              onChange={(e) =>
                onPick(
                  delivery.delivery_ref,
                  { collector: { ...collector, phone: e.target.value } },
                  { preview: false },
                )
              }
              onBlur={() => onPick(delivery.delivery_ref, { collector })}
            />
            {errors.recipient_phone && (
              <p
                id={`${id}-phone-error`}
                className="text-red-700 dark:text-red-400"
              >
                {errors.recipient_phone}
              </p>
            )}
          </div>
        </div>
      </fieldset>
    </div>
  );
}
